/**
 * DOM 기반 테이블 셀 선택 훅.
 *
 * selectable td 계약 (selectableCellAttrs 로만 세팅할 것):
 * - id: DOM에서 유일한 선택 키
 * - data-cell-id: 저장/편집 경계에서 쓰는 도메인 셀 키
 * - data-display-value: 복사에 사용하는 표시값 (필수 — 없으면 Sentry 보고 + 빈 문자열)
 */

import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, type RefObject } from "react";
import { toast } from "sonner";
import { toastApplyResult } from "@/components/common/apply-result-toast";

const DEFAULT_SELECTION_STYLE_ID = "table-cell-selection-style";
const ROW_THRESHOLD = 10;

// ── 드래그 자동 스크롤 상수 ──
/** 스크롤 뷰포트 가장자리에서 자동 스크롤이 발동하는 폭(px) */
export const AUTO_SCROLL_EDGE_SIZE = 48;
/**
 * 누른 지점에서 이만큼 움직이기 전엔 드래그가 아니라 클릭이다 — 자동 스크롤을 시작하지 않는다.
 * 셀이 스크롤 영역 가장자리(edge zone) 안에 놓여 있을 때 그냥 누르기만 해도 스크롤이 달려
 * 내용이 손 밑에서 움직이고, 그 mouseover 로 범위가 늘어나 다중 선택이 되던 문제
 */
export const DRAG_START_THRESHOLD_PX = 4;

export const hasMovedPastDragThreshold = (
  origin: { x: number; y: number },
  pointer: { x: number; y: number },
) =>
  Math.hypot(pointer.x - origin.x, pointer.y - origin.y) >=
  DRAG_START_THRESHOLD_PX;
/** 프레임당 최대 스크롤량(px) — 가장자리에 붙거나 뷰포트 밖이면 이 속도 */
export const AUTO_SCROLL_MAX_SPEED = 22;
/** 스크롤 가능/영역 잔여 판정의 반올림 오차 허용치(px) */
const SCROLL_EPSILON = 1;
/** 포인터를 뷰포트 안쪽으로 클램프할 때 쓰는 인셋(px) */
const EDGE_INSET = 1;

// 한 화면에 선택 훅이 여러 개 마운트되어도 document 이벤트는 마지막으로 선택을
// 시작한 인스턴스 하나만 처리한다. 비교 화면처럼 기간별 renderer가 많은 경우
// 모든 key/mouse 이벤트를 수백 개 훅이 중복 처리하는 비용을 막는다.
let activeSelectionOwner: symbol | null = null;
const clearSelectionByOwner = new Map<symbol, () => void>();
const styleOwnersById = new Map<string, Set<symbol>>();

const activateSelectionOwner = (owner: symbol) => {
  if (activeSelectionOwner === owner) return;
  if (activeSelectionOwner) {
    clearSelectionByOwner.get(activeSelectionOwner)?.();
  }
  activeSelectionOwner = owner;
};

const registerStyleOwner = (styleId: string, owner: symbol) => {
  const owners = styleOwnersById.get(styleId) ?? new Set<symbol>();
  owners.add(owner);
  styleOwnersById.set(styleId, owners);
};

const unregisterStyleOwner = (styleId: string, owner: symbol): boolean => {
  const owners = styleOwnersById.get(styleId);
  if (!owners) return true;
  owners.delete(owner);
  if (owners.size > 0) return false;
  styleOwnersById.delete(styleId);
  return true;
};

/** 선택된 셀의 기본 시각 강조(파란 배경). `selectionCss` 옵션으로 표별 교체 가능. */
const DEFAULT_SELECTED_CELL_CSS =
  "background-color:rgb(191 219 254)!important;position:relative;z-index:10;cursor:cell;user-select:none";

/**
 * 자체 클릭 액션·상태 배경색을 가진 표(DSD 계열)용 다중 선택 강조: 셀별 검은 테두리.
 *
 * outline 을 셀 경계선 중앙에 걸친다(offset -1 = 안쪽 1px + 바깥쪽 1px). 인접한 두
 * 선택 셀은 서로 다른 밴드를 이어 붙이는 게 아니라 **같은 2px 밴드를 겹쳐** 그리고,
 * 나중에 페인트되는 셀이 밴드 전체를 다시 그리므로 공유 변이 두꺼워질 수 없다 —
 * 셀 안쪽으로만 2px 씩 그리던 offset -2 시절엔 공유 변이 4px 로 보였다.
 *
 * inset box-shadow 로 안팎 1px 씩 그리는 방식은 쓰지 않는다: 안쪽 링이 패딩 박스에
 * 클리핑되어, 변마다 보더 유무가 다른 셀(kit 의 border-b/border-r)에서 링 위치가
 * 변별로 어긋나 줄무늬·불균일이 생긴다. outline 은 보더/패딩과 무관하게 그려진다.
 * 셀마다 온전한 사각형이라 병합 셀(rowspan/colspan)에도 이웃 계산 없이 성립한다.
 *
 * 배경 음영은 얹지 않는다 — 이 표들의 셀 배경은 상태(match/diff·합계불일치)와 ghost
 * 빗금이 이미 쓰고 있어, 선택까지 배경으로 표시하면 그 정보와 경쟁한다. 선택은 테두리
 * 하나로만 나타낸다.
 */
export const MULTI_SELECT_CELL_BORDER_CSS =
  "outline:2px solid rgb(0 0 0)!important;outline-offset:-1px;" +
  "position:relative;z-index:10;user-select:none";

/**
 * `autoBindContainer` 표의 기본 셀 셀렉터. 헤더(th)는 기본적으로 선택 대상이 아니다 —
 * `includeHeaderCells` 를 켠 표에서만 아래 header-inclusive 변형을 쓴다.
 */
const DEFAULT_CELL_SELECTOR = "td[data-cell-id][id]";
const DEFAULT_CELL_SELECTOR_WITH_HEADER =
  "td[data-cell-id][id], th[data-cell-id][id]";

/** 시작 표 안에서의 범위 선택/그리드 구성용 셀렉터(id 가 곧 선택 자격). */
const TABLE_SCOPED_CELL_SELECTOR = "td[id]";
const TABLE_SCOPED_CELL_SELECTOR_WITH_HEADER = "td[id], th[id]";

interface TableGrid {
  grid: HTMLTableCellElement[][];
  findCell: (cell: HTMLTableCellElement) => { row: number; col: number } | null;
}

type SelectableCellElement = HTMLElement;
type ScrollAxis = "x" | "y";

interface PointerPosition {
  x: number;
  y: number;
}

interface ViewportBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * 드래그 한 번 동안 불변인 정보. beginSelection 에서 1회 계산해 캐싱한다
 * (매 프레임 부모 체인을 다시 순회하지 않기 위함).
 */
interface DragSession {
  scrollX: HTMLElement | null;
  scrollY: HTMLElement | null;
  /** 자동 스크롤 범위의 루트(시작 테이블, 없으면 컨테이너) — 표 경계를 넘지 않는다 */
  root: HTMLElement;
  /** 드래그 타겟이 될 수 있는 셀 셀렉터 */
  selector: string;
  /** 폴백 최근접 스캔용 셀 목록 — 첫 폴백 시 1회만 조회해 캐싱 */
  cells: SelectableCellElement[] | null;
}

export interface TableCellSelectionEntry {
  cellId: string;
  uniqueId: string;
  element: SelectableCellElement;
}

export interface TableCellPasteEntry extends TableCellSelectionEntry {
  value: string;
}

/** 붙여넣기 적용 결과. 값이 같아 건너뛴 셀은 어느 쪽에도 세지 않는다. */
export interface TableCellPasteResult {
  /** 실제로 값이 바뀐 셀 수 */
  applied: number;
  /** 검증 실패·읽기 전용 등으로 반영되지 않은 셀 수 */
  failed: number;
}

/**
 * 따옴표로 열린 필드의 닫는 따옴표 위치를 찾는다. `""` 는 리터럴 따옴표(Excel 관례)라
 * 건너뛴다. 닫는 따옴표가 없으면 -1 — 인용 필드가 아니라 따옴표로 시작하는 일반 텍스트.
 */
const findQuotedFieldEnd = (text: string, from: number): number => {
  let i = from;
  while (i < text.length) {
    if (text[i] !== '"') {
      i += 1;
      continue;
    }
    if (text[i + 1] === '"') {
      i += 2;
      continue;
    }
    return i;
  }
  return -1;
};

/**
 * 복사할 표의 모양. 두 모드 모두 TSV 인용으로 셀 내부 줄바꿈·탭·따옴표를 보존한다.
 * - `"values"` (기본) — 표시값을 화면 좌표 순서로 복사한다.
 * - `"excel-table"` — 그리드 좌표와 HTML로 병합·테두리·헤더 배경도 보존한다.
 */
export type ClipboardTableFormat = "values" | "excel-table";

/**
 * 클립보드 텍스트를 붙여넣기용 2차원 배열로 파싱한다.
 * 외부 편집기(Excel 등)는 복사 시 끝에 줄바꿈을 붙이는데,
 * 그대로 split 하면 빈 행이 생겨 다음 행 첫 셀을 빈 값으로 덮으므로 제거한다.
 *
 * 셀 안에 개행·탭이 있는 값은 Excel(과 우리 직렬화)이 따옴표로 감싸므로,
 * 필드 첫 글자가 따옴표면 인용 필드로 읽는다 — 단순 split 하면 셀 내부 개행이
 * 행 구분자로 오인돼 그리드가 어긋난다.
 */
export const parsePastedClipboardText = (clipText: string): string[][] => {
  // ponytail: "" 는 [[""]] 로 파싱한다. trailing 줄바꿈 없는 스프레드시트에서
  // 빈 셀 1개를 복사하면 "" 가 오는데, 붙여넣기로 셀을 지우는 게 스프레드시트
  // 표준 동작. 진짜 빈 클립보드와 구분 불가하지만 오동작은 undo 로 복구 가능.
  const text = clipText.replace(/\r\n?/g, "\n").replace(/\n$/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let i = 0;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i];
    if (
      ch === '"' &&
      (i === 0 || text[i - 1] === "\t" || text[i - 1] === "\n")
    ) {
      const closing = findQuotedFieldEnd(text, i + 1);
      const inner = closing === -1 ? null : text.slice(i + 1, closing);
      // 인용을 풀 이유(개행·탭·이스케이프된 따옴표)가 안쪽에 있을 때만 인용 필드로 본다.
      // 스프레드시트는 `"주1"` 처럼 따옴표로 감싼 리터럴을 평문에 그대로 내보내는데,
      // 그걸 인용으로 풀면 따옴표가 사라진다. 우리가 감싼 값은 항상 그 이유를 담고 있다.
      if (
        inner !== null &&
        (closing === text.length - 1 || /[\t\n]/.test(text[closing + 1])) &&
        /[\t\n]|""/.test(inner)
      ) {
        field = inner.replace(/""/g, '"');
        i = closing + 1;
        continue;
      }
      // 인용 필드가 아니면 따옴표로 시작하는 일반 텍스트로 취급한다.
    }
    if (ch === "\t") {
      endField();
      i += 1;
      continue;
    }
    if (ch === "\n") {
      endRow();
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  endRow();
  return rows;
};

/**
 * TSV의 셀 경계와 값을 구별하도록 줄바꿈·탭·따옴표가 포함된 필드를 인용한다.
 */
const escapeClipboardField = (value: string): string => {
  const normalized = value.replace(/\r\n?/g, "\n");
  return /[\n\t"]/.test(normalized)
    ? `"${normalized.replace(/"/g, '""')}"`
    : normalized;
};

/** parsePastedClipboardText 와 짝이 되는 직렬화. trailing \n 은 TSV 관례이자
 *  마지막 행이 빈 셀일 때 행 구분자를 잃지 않기 위한 것. */
export const serializeRowsToClipboardText = (rows: string[][]): string =>
  rows.map((row) => row.map(escapeClipboardField).join("\t")).join("\n") + "\n";

export interface UseTableCellSelectionOptions {
  styleId?: string;
  onEnterEdit?: (
    cellId: string,
    uniqueId?: string,
    cell?: SelectableCellElement,
  ) => void;
  /**
   * 붙여넣을 값을 받아 적용하고 성공·실패 건수를 돌려준다. 실패 사유 안내는 소비처의
   * 몫이고, 훅은 건수 요약만 띄운다. 둘 다 0이면 "기존 값과 같음"으로 안내한다.
   * 실패만 있으면 선택을 바꾸지 않는다.
   */
  onPasteValues?: (entries: TableCellPasteEntry[]) => TableCellPasteResult;
  onDeleteValues?: (
    cellIds: string[],
    cells: TableCellSelectionEntry[],
  ) => void;
  onUndo?: () => void;
  onSelectionChange?: (
    uniqueIds: string[],
    cells: TableCellSelectionEntry[],
  ) => void;
  enableRelatedCellSelection?: boolean;
  /**
   * 선택 상태는 그대로 추적/전파하되 셀에 시각 강조(배경색)는 칠하지 않는다.
   * 검토 모드처럼 호출부가 자체 강조 스타일을 별도로 입히는 경우 사용한다.
   */
  disableSelectionStyle?: boolean;
  /**
   * 컨테이너에 위임(delegation) 방식으로 드래그 선택 이벤트를 직접 바인딩한다.
   * 셀마다 핸들러를 prop 으로 넘기지 않아도 `cellSelector` 에 매칭되는 셀이면 자동 선택된다.
   * 새 표에 드래그 선택을 붙일 때는 이 옵션만 켜면 된다(IXD 처럼 셀별 핸들러를 직접
   * 다는 기존 방식은 이 옵션 없이 handleCell* 를 그대로 사용한다).
   */
  autoBindContainer?: boolean;
  /** `autoBindContainer` 시 선택 가능한 셀을 식별하는 CSS 셀렉터. 기본 `td[data-cell-id][id]`. */
  cellSelector?: string;
  /**
   * 헤더 셀(th)도 드래그 선택/복사 대상에 포함한다. 기본 false — 헤더는 값이 아니라
   * 표의 축이라 대부분의 표에서 선택 대상이 아니고, 켜면 그 표의 th 렌더 지점이
   * `selectableCellAttrs` 계약(id·data-cell-id·data-display-value)을 지켜야 한다.
   * 현재 사용처: DSD 리뷰, DSD-DSD 대사.
   */
  includeHeaderCells?: boolean;
  /**
   * 표를 클립보드로 주고받는 규격. 기본 `"values"` — 표시값과 셀 내부 줄바꿈을 보존한다.
   * `"excel-table"` 은 병합·서식을 살려 엑셀과 표를 주고받는다(`ClipboardTableFormat`).
   * 현재 사용처: DSD 리뷰, DSD-DSD 대사, 최종산출물·값 채우기의 DSD 원본 표.
   */
  clipboardTableFormat?: ClipboardTableFormat;
  /**
   * 같은 표 안 드래그를 엑셀식 그리드 범위로 선택한다(병합 셀 closure 포함).
   * 기본 false — rect 겹침율 방식(기존 동작)을 유지한다. 병합 셀이 많은 표에서
   * 전폭 colspan 셀이 선택에서 빠져 범위가 끊기는 문제를 없애려 켠다.
   * 현재 사용처: DSD 리뷰, DSD-DSD 대사.
   */
  enableGridRangeSelection?: boolean;
  /**
   * 방향키로 선택 셀을 이동/확장하는 기능. 자체 방향키 navigation 을 가진 표(예: DSD)는
   * false 로 꺼서 충돌을 피한다. 기본 true(IXD 등 기존 동작 유지).
   */
  enableArrowNavigation?: boolean;
  /**
   * 선택된 셀에 입힐 CSS 선언(예: 파란 배경 대신 black border). 표별로 강조 모양을 바꾼다.
   * 미지정 시 기본 파란 배경(DEFAULT_SELECTED_CELL_CSS)을 사용한다.
   */
  selectionCss?: string;
  /**
   * 선택 셀 수가 이 값 미만이면 시각 강조(selectionCss)를 칠하지 않는다(기본 1 = 항상 강조).
   * 선택 추적/`onSelectionChange` 전파는 그대로 유지되므로, 드래그로 1→2+ 로 커지면
   * 자연스럽게 강조가 켜진다. 예: DSD 는 2 로 설정해 단일 클릭(=size 1)에는 검은 테두리를
   * 띄우지 않고 navigation focus(ring)만 쓰게 한다.
   */
  minSelectionToStyle?: number;
}

interface SelectCellOptions {
  scroll?: boolean;
}

function buildTableGrid(
  table: HTMLTableElement,
  cellSelector: string = TABLE_SCOPED_CELL_SELECTOR,
): TableGrid {
  const allTds = Array.from(
    table.querySelectorAll(cellSelector),
  ) as HTMLTableCellElement[];

  const cellsWithRect = allTds.map((td) => ({
    td,
    rect: td.getBoundingClientRect(),
  }));
  cellsWithRect.sort((a, b) => {
    const rowDiff = a.rect.top - b.rect.top;
    if (Math.abs(rowDiff) > ROW_THRESHOLD) return rowDiff;
    return a.rect.left - b.rect.left;
  });

  const grid: HTMLTableCellElement[][] = [];
  let currentRowTop = -Infinity;
  cellsWithRect.forEach(({ td, rect }) => {
    if (Math.abs(rect.top - currentRowTop) > ROW_THRESHOLD) {
      grid.push([]);
      currentRowTop = rect.top;
    }
    grid[grid.length - 1].push(td);
  });

  const findCell = (cell: HTMLTableCellElement) => {
    for (let r = 0; r < grid.length; r += 1) {
      const c = grid[r].indexOf(cell);
      if (c !== -1) return { row: r, col: c };
    }
    return null;
  };

  return { grid, findCell };
}

/** 입력 요소 안에서의 키/붙여넣기는 브라우저 기본 동작에 맡긴다. */
const isEditableTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLElement && target.isContentEditable);

function findTopLeftCell(selected: Set<string>): SelectableCellElement | null {
  let best: SelectableCellElement | null = null;
  let bestRect: DOMRect | null = null;
  selected.forEach((uid) => {
    const el = document.getElementById(uid);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (
      !bestRect ||
      rect.top < bestRect.top - 5 ||
      (Math.abs(rect.top - bestRect.top) < 5 && rect.left < bestRect.left)
    ) {
      best = el;
      bestRect = rect;
    }
  });
  return best;
}

const getSelectionEntries = (
  selected: Set<string>,
): TableCellSelectionEntry[] => {
  const entries: TableCellSelectionEntry[] = [];
  for (const uniqueId of selected) {
    const element = document.getElementById(uniqueId);
    const cellId = element?.getAttribute("data-cell-id");
    if (!element || !cellId) continue;
    entries.push({ cellId, uniqueId, element });
  }
  return entries;
};

/**
 * 선택 가능 셀의 속성 계약. 렌더 지점은 이 헬퍼로만 속성을 세팅한다 —
 * displayValue 누락이 컴파일 에러가 되도록 강제한다(값이 없는 셀은 "" 명시).
 * 복사값은 데이터(data-display-value)에서 나오며, 표현(textContent)에서 만들지 않는다.
 */
export const selectableCellAttrs = (p: {
  uniqueId: string;
  cellId: string;
  displayValue: string;
}) => ({
  id: p.uniqueId,
  "data-cell-id": p.cellId,
  "data-display-value": p.displayValue,
});

/**
 * 복사값을 읽는 단일 지점. 셀 안 줄바꿈은 LF 로 정규화한다 — DSD 원문의 강제 개행은
 * `\r\n` 으로 실려 오는데, 엑셀의 셀 내 줄바꿈은 LF 라서 CR 이 섞이면 그 지점에서
 * 잘려 첫 줄만 보인다(HTML 경로에서도 `\r` 이 `<br>` 앞에 남는다).
 */
export const getClipboardText = (cell: SelectableCellElement): string =>
  (cell.getAttribute("data-display-value") ?? "").replace(/\r\n?/g, "\n");

// ── 병합(span) 인지 그리드 — 엑셀식 드래그 선택·복사의 기반 ──
// DSD 표는 rowspan/colspan 병합 셀이 흔하다. 화면 rect 만으로는 "이 셀이 그리드의
// 몇 행 몇 열을 차지하는가"를 알 수 없어, 선택 확장과 복사 정렬이 병합 셀에서 깨진다.

/** 표의 rowSpan/colSpan 을 펼친 점유 그리드. `occupancy[r][c]` = 그 좌표를 덮는 셀. */
interface SpanGrid {
  occupancy: (HTMLTableCellElement | null)[][];
  anchorOf: Map<HTMLTableCellElement, { row: number; col: number }>;
}

interface GridRange {
  r1: number;
  r2: number;
  c1: number;
  c2: number;
}

export const buildSpanGrid = (table: HTMLTableElement): SpanGrid => {
  const occupancy: (HTMLTableCellElement | null)[][] = [];
  const anchorOf = new Map<
    HTMLTableCellElement,
    { row: number; col: number }
  >();
  const rows = Array.from(table.rows);
  for (let r = 0; r < rows.length; r += 1) {
    occupancy[r] ??= [];
    let c = 0;
    for (const cell of Array.from(rows[r].cells)) {
      // 윗 행 rowspan 이 이미 차지한 칸은 건너뛰고 다음 빈 칸에 배치 (표준 표 배치 규칙)
      while (occupancy[r][c]) c += 1;
      anchorOf.set(cell, { row: r, col: c });
      const rowSpan = Math.max(1, cell.rowSpan);
      const colSpan = Math.max(1, cell.colSpan);
      for (let dr = 0; dr < rowSpan; dr += 1) {
        occupancy[r + dr] ??= [];
        for (let dc = 0; dc < colSpan; dc += 1) {
          occupancy[r + dr][c + dc] = cell;
        }
      }
      c += colSpan;
    }
  }
  return { occupancy, anchorOf };
};

/** 셀이 차지하는 그리드 사각 영역(anchor + span). 그리드에 없는 요소면 null. */
const cellFootprint = (
  grid: SpanGrid,
  cell: HTMLTableCellElement,
): GridRange | null => {
  const anchor = grid.anchorOf.get(cell);
  if (!anchor) return null;
  return {
    r1: anchor.row,
    r2: anchor.row + Math.max(1, cell.rowSpan) - 1,
    c1: anchor.col,
    c2: anchor.col + Math.max(1, cell.colSpan) - 1,
  };
};

/**
 * 범위에 일부라도 걸친 병합 셀의 footprint 전체가 들어올 때까지 사각 범위를 넓힌다 —
 * 엑셀이 병합 셀을 지나는 선택을 직사각형으로 닫는 것과 동일한 규칙.
 */
const expandRangeOverSpans = (grid: SpanGrid, range: GridRange): GridRange => {
  const result = { ...range };
  let changed = true;
  while (changed) {
    changed = false;
    for (let r = result.r1; r <= result.r2; r += 1) {
      const row = grid.occupancy[r];
      if (!row) continue;
      for (let c = result.c1; c <= result.c2; c += 1) {
        const cell = row[c];
        if (!cell) continue;
        const fp = cellFootprint(grid, cell);
        if (!fp) continue;
        if (fp.r1 < result.r1) {
          result.r1 = fp.r1;
          changed = true;
        }
        if (fp.r2 > result.r2) {
          result.r2 = fp.r2;
          changed = true;
        }
        if (fp.c1 < result.c1) {
          result.c1 = fp.c1;
          changed = true;
        }
        if (fp.c2 > result.c2) {
          result.c2 = fp.c2;
          changed = true;
        }
      }
    }
  }
  return result;
};

/**
 * 같은 표 안 드래그의 선택 집합 — 엑셀 시맨틱. anchor~target 이 이루는 그리드 사각
 * 범위를 병합 셀 closure 로 닫은 뒤, 범위 안 선택 가능 셀(id 보유)의 id 집합을 만든다.
 * rect 겹침율 방식은 전폭 colspan 셀이 좁은 드래그와 33%만 겹쳐 선택에서 빠지고,
 * 선택이 중간에 끊긴 두 박스가 된다. 같은 표가 아니면 null — 호출부가 rect 폴백.
 */
export const collectGridRangeSelection = (
  start: SelectableCellElement,
  target: SelectableCellElement,
  cellSelector: string = TABLE_SCOPED_CELL_SELECTOR,
): Set<string> | null => {
  if (
    !(start instanceof HTMLTableCellElement) ||
    !(target instanceof HTMLTableCellElement)
  ) {
    return null;
  }
  const table = start.closest("table");
  if (!table || target.closest("table") !== table) return null;

  const grid = buildSpanGrid(table);
  const startFp = cellFootprint(grid, start);
  const targetFp = cellFootprint(grid, target);
  if (!startFp || !targetFp) return null;

  const range = expandRangeOverSpans(grid, {
    r1: Math.min(startFp.r1, targetFp.r1),
    r2: Math.max(startFp.r2, targetFp.r2),
    c1: Math.min(startFp.c1, targetFp.c1),
    c2: Math.max(startFp.c2, targetFp.c2),
  });

  const selected = new Set<string>();
  for (let r = range.r1; r <= range.r2; r += 1) {
    for (let c = range.c1; c <= range.c2; c += 1) {
      const cell = grid.occupancy[r]?.[c];
      // 범위 안이어도 그 표의 선택 자격(cellSelector)을 만족해야 선택된다 —
      // 헤더 미포함 표에서 th 가 딸려 들어오지 않게.
      if (cell?.id && cell.matches(cellSelector)) selected.add(cell.id);
    }
  }
  return selected;
};

interface GridSelection {
  grid: SpanGrid;
  selected: Set<HTMLTableCellElement>;
  bounds: GridRange;
}

/**
 * 선택 셀들을 한 표의 그리드 좌표계로 환산한다. 선택이 한 표 안이 아니면 null —
 * 호출부가 화면 좌표 기반 폴백으로 넘어간다.
 */
const resolveGridSelection = (
  cells: SelectableCellElement[],
  format: ClipboardTableFormat,
): GridSelection | null => {
  const first = cells[0];
  if (!(first instanceof HTMLTableCellElement)) return null;
  const table = first.closest("table");
  if (!table) return null;

  const selected = new Set<HTMLTableCellElement>();
  for (const cell of cells) {
    if (
      !(cell instanceof HTMLTableCellElement) ||
      cell.closest("table") !== table
    ) {
      return null;
    }
    selected.add(cell);
  }

  // 범위는 선택 셀의 **anchor** 들이 이루는 사각형이다 — span 끝(footprint)이 아니라.
  //
  // footprint 로 잡으면, 아래로 걸친 병합 셀 하나가 선택하지도 않은 행까지 범위를 늘린다.
  // 그 행엔 값을 실을 anchor 가 없어 빈 칸만 찍히고, 붙여넣기에 **빈 줄이 하나 더** 생긴다
  // (실측: DSD 합계 행의 라벨 셀이 아래 문단 행 `desc-foot` 까지 rowspan 으로 걸친 표).
  // 열도 같다 — 오른쪽으로 삐져나온 colspan 이 빈 열을 만든다.
  //
  // anchor 로 잡아도 병합은 안 깨진다: 병합 셀이 덮는 행·열에 선택된 셀이 하나라도 있으면
  // 그 셀의 anchor 가 범위를 거기까지 넓혀주고, span 은 `Math.min(fp.r2, bounds.r2)` 로
  // 범위에 맞춰 잘린다. 아무도 없는 바깥쪽만 잘려나간다.
  const grid = buildSpanGrid(table);
  let bounds: GridRange | null = null;
  for (const cell of selected) {
    const fp = cellFootprint(grid, cell);
    if (!fp) return null;
    // "excel-table" 은 span 끝이 아니라 anchor 만 본다(위 주석). 기존 동작은 footprint.
    const box =
      format === "excel-table"
        ? { r1: fp.r1, r2: fp.r1, c1: fp.c1, c2: fp.c1 }
        : fp;
    bounds = bounds
      ? {
          r1: Math.min(bounds.r1, box.r1),
          r2: Math.max(bounds.r2, box.r2),
          c1: Math.min(bounds.c1, box.c1),
          c2: Math.max(bounds.c2, box.c2),
        }
      : box;
  }
  if (!bounds) return null;
  return { grid, selected, bounds };
};

/**
 * 선택 셀들을 그리드 좌표에 배치한 클립보드 행렬. 병합 셀은 anchor 위치에만 값을
 * 싣고 나머지 footprint 는 "" 로 패딩한다 — 엑셀이 병합 셀을 복사하는 형식과 같아,
 * 붙여넣은 뒤 다시 병합하기 쉬운 모양이 된다. 패딩 없이 값만 이어 붙이면 2단 헤더
 * (colspan)·병합 행이 왼쪽으로 밀려 열이 전부 어긋난다.
 * 선택이 한 표 안이 아니면 null — 호출부가 화면 좌표 기반 행 묶기로 폴백한다.
 */
export const buildGridClipboardRows = (
  cells: SelectableCellElement[],
  format: ClipboardTableFormat = "values",
): string[][] | null => {
  const resolved = resolveGridSelection(cells, format);
  if (!resolved) return null;
  const { grid, selected, bounds } = resolved;

  const rows: string[][] = [];
  for (let r = bounds.r1; r <= bounds.r2; r += 1) {
    const row: string[] = [];
    for (let c = bounds.c1; c <= bounds.c2; c += 1) {
      const cell = grid.occupancy[r]?.[c] ?? null;
      const anchor = cell ? grid.anchorOf.get(cell) : undefined;
      const isAnchorPosition = !!anchor && anchor.row === r && anchor.col === c;
      row.push(
        cell && isAnchorPosition && selected.has(cell)
          ? getClipboardText(cell)
          : "",
      );
    }
    rows.push(row);
  }
  return rows;
};

/**
 * 붙여넣은 표의 셀 테두리 — 엑셀의 "모든 테두리"(가는 실선)와 같은 굵기.
 *
 * 단위가 `pt` 인 이유: 엑셀은 CSS 굵기를 자기 테두리 두께(thin/medium/thick)로 매핑하는데,
 * 그 기준이 엑셀 자신이 "웹 페이지로 저장"할 때 쓰는 dialect 다 — thin 은 `0.5pt`,
 * medium 은 `1.0pt`. `1px`(=0.75pt)는 thin 과 medium 사이라 medium 으로 올라붙어
 * **모든 선이 굵게 붙여넣어졌다**(실측: 색을 바꿔도 굵기는 그대로였다).
 */
const CLIPBOARD_CELL_BORDER_STYLE = "border:0.5pt solid black";
/** 헤더 셀 배경. */
const CLIPBOARD_HEADER_FILL = "#DCDCDC";

/**
 * 회색으로 채울 헤더 셀 판별 — **화면에서 회색인 셀만** 회색으로 나가게 한다.
 *
 * 두 신호를 본다:
 * - `<th>` — 프론트가 그리는 표(DSD 대사·리뷰)의 열 머리글.
 * - 인라인 `background-color` — 백엔드 HTML 표(최종산출물·값 채우기)는 전부 `<td>` 라
 *   태그로는 못 가린다. 대신 DSD 원문이 머리글 밴드에 칠한 `#dcdcdc` 가 셀의 style
 *   속성에 그대로 실려 온다(`dsd_report_html_renderer` 는 원문 style 을 통과시킨다).
 *   Tailwind 클래스로 칠하는 대사 화면의 변경 상태 배경은 인라인이 아니라 여기 안 걸린다.
 *
 * 백엔드의 `data-type="HEADER"` 는 쓰지 않는다. 그건 의미상 "머리글 성격"이라 행 머리글
 * ("정비 사업" 등)에도 붙고, 그걸 따르면 화면에선 흰색인 셀까지 회색이 된다.
 */
const isHeaderCellForClipboard = (cell: HTMLTableCellElement): boolean =>
  cell.tagName === "TH" || cell.style.backgroundColor !== "";

/**
 * 일반 `<br>`는 Excel에서 행 경계로 해석될 수 있어 같은 셀 안의 개행임을 명시한다.
 */
const escapeHtmlText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r\n?|\n/g, '<br style="mso-data-placement:same-cell">');

/**
 * 선택 영역을 `<table>` 마크업으로 직렬화한다. 클립보드에 `text/html` 로 함께 실으면
 * 엑셀·구글시트가 이쪽을 우선 읽어 **colspan/rowspan 을 실제 병합 셀로 복원**한다
 * (TSV 만 실으면 병합 정보가 사라져 값이 빈 칸으로 흩어진 표가 된다).
 *
 * 병합에 덮인 좌표는 셀을 만들지 않는다 — HTML 표에서는 span 이 그 자리를 차지하므로,
 * TSV 처럼 "" 로 패딩하면 열이 하나씩 밀린다. 선택 밖으로 삐져나온 span 은 선택 범위에
 * 맞춰 잘라 표를 직사각형으로 유지한다.
 * 선택이 한 표 안이 아니면 null — 그때는 text/plain 만 싣는다.
 */
export const buildGridClipboardHtml = (
  cells: SelectableCellElement[],
): string | null => {
  // 이 마크업 자체가 "excel-table" 규격에서만 실린다.
  const resolved = resolveGridSelection(cells, "excel-table");
  if (!resolved) return null;
  const { grid, selected, bounds } = resolved;

  // 이미 앞선 td 의 span 이 차지한 좌표. HTML 표는 덮인 자리에 셀을 쓰지 않으므로,
  // "아직 안 채워진 첫 좌표"에서만 td 를 열고 그 span 이 덮는 범위를 소비 표시한다.
  const consumed = new Set<string>();
  const rows: string[] = [];
  for (let r = bounds.r1; r <= bounds.r2; r += 1) {
    const tds: string[] = [];
    for (let c = bounds.c1; c <= bounds.c2; c += 1) {
      if (consumed.has(`${r}:${c}`)) continue;

      const cell = grid.occupancy[r]?.[c] ?? null;
      const fp = cell ? cellFootprint(grid, cell) : null;
      // span 은 선택 범위에 맞춰 자른다. anchor 가 범위 밖(위/왼쪽)인 병합 셀은
      // 지금 좌표부터 남은 부분만 차지하므로 여기서 자연히 처리된다.
      const rowSpan = fp ? Math.min(fp.r2, bounds.r2) - r + 1 : 1;
      const colSpan = fp ? Math.min(fp.c2, bounds.c2) - c + 1 : 1;
      for (let dr = 0; dr < rowSpan; dr += 1) {
        for (let dc = 0; dc < colSpan; dc += 1) {
          consumed.add(`${r + dr}:${c + dc}`);
        }
      }

      const anchor = cell ? grid.anchorOf.get(cell) : undefined;
      const isAnchorPosition = !!anchor && anchor.row === r && anchor.col === c;
      // 값은 anchor 자리에서만, 그리고 선택된 셀만 싣는다 — 범위에 걸치기만 한
      // 병합 셀은 자리(빈 칸)만 유지해 표 모양을 지킨다.
      const text =
        cell && isAnchorPosition && selected.has(cell)
          ? escapeHtmlText(getClipboardText(cell))
          : "";
      const attrs =
        (rowSpan > 1 ? ` rowspan="${rowSpan}"` : "") +
        (colSpan > 1 ? ` colspan="${colSpan}"` : "");
      // 헤더 셀은 원본 표와 같이 회색으로 채워, 붙여넣은 표에서도 축이 구분된다.
      const style =
        cell && isHeaderCellForClipboard(cell)
          ? `${CLIPBOARD_CELL_BORDER_STYLE};background-color:${CLIPBOARD_HEADER_FILL}`
          : CLIPBOARD_CELL_BORDER_STYLE;
      tds.push(`<td${attrs} style="${style}">${text}</td>`);
    }
    rows.push(`<tr>${tds.join("")}</tr>`);
  }
  return (
    `<table style="border-collapse:collapse"><tbody>` +
    `${rows.join("")}</tbody></table>`
  );
};

/**
 * 여러 셀의 excel-table 복사는 병합·서식을 싣는다. 그 외에도 셀 안에 개행이나 따옴표가
 * 있으면 테두리·배경 없는 HTML 표를 동봉해, 평문 인용을 풀지 않는 스프레드시트에도
 * 원문 값이 들어가게 한다.
 */
export const buildCopyClipboardHtml = (
  cells: SelectableCellElement[],
  format: ClipboardTableFormat = "values",
  rows?: string[][],
): string | null => {
  if (format === "excel-table" && cells.length > 1) {
    const html = buildGridClipboardHtml(cells);
    if (html) return html;
  }
  // 줄바꿈·따옴표가 든 값은 평문(TSV)에서 인용되는데, 엑셀·구글시트는 그 인용을 풀지
  // 않고 그대로 넣는다(실측). HTML 을 같이 실으면 그쪽을 먼저 읽어 원문 값이 들어간다.
  const valueRows = rows ?? buildRectClipboardRows(cells);
  if (!valueRows.some((row) => row.some((value) => /[\r\n"]/.test(value)))) {
    return null;
  }
  return `<table><tbody>${valueRows
    .map(
      (row) =>
        `<tr>${row.map((value) => `<td>${escapeHtmlText(value)}</td>`).join("")}</tr>`,
    )
    .join("")}</tbody></table>`;
};

// 클립보드 HTML 은 `<table>` 조각 그대로 싣는다 — 브라우저가 웹 표를 복사할 때
// 내보내는 형태와 같다.
//
// 한때 엑셀 인식률을 높이려고 `<html xmlns:x=...><head><meta ProgId="Excel.Sheet">...`
// 전문으로 감쌌는데, 그게 오히려 붙여넣기를 깨뜨렸다: 브라우저가 우리 마크업 **앞에**
// 자기 `<meta charset>` 을 붙이는 바람에 `<meta>` 뒤에 `<html>` 이 오는 깨진 문서가 되고,
// 엑셀이 파싱을 포기해 평문(TSV)으로 떨어졌다 — 병합이 통째로 사라졌다.
// 같은 환경(맥 브라우저 → Parallels 윈도우 엑셀)에서 위키백과 표의 네이티브 복사는
// 병합·배경색까지 정상 복원됐고, 그 마크업이 바로 전문 없는 표 조각이었다.
//
// 참고: Excel for Mac 은 HTML 을 읽되 병합을 무시해 값이 왼쪽으로 몰린다. 붙여넣기
// 옵션에서 "텍스트"를 고르면 TSV(병합 자리 빈 칸 패딩)로 들어가 정렬이 맞는다.
// 복사 시점에 붙여넣을 앱을 알 수 없으므로 플랫폼 분기는 두지 않는다.

/**
 * `copy` 이벤트로 클립보드를 채운다. 사용자 제스처(keydown) 안에서 **동기로** 실행돼야
 * 하며, 새니타이저를 거치지 않아 위 엑셀 전문이 그대로 전달된다.
 * 성공하면 true. execCommand 는 선택 영역이 없으면 실패하므로 화면 밖 임시 노드를
 * 잠깐 선택했다가 원래 선택을 되돌린다.
 */
const writeClipboardViaCopyEvent = (
  text: string,
  html: string | null,
): boolean => {
  if (!document.queryCommandSupported?.("copy")) return false;

  let handled = false;
  const onCopy = (event: Event) => {
    const clipboardData = (event as ClipboardEvent).clipboardData;
    if (!clipboardData) return;
    event.preventDefault();
    clipboardData.setData("text/plain", text);
    if (html) clipboardData.setData("text/html", html);
    handled = true;
  };

  const selection = window.getSelection();
  const previousRanges: Range[] = [];
  for (let i = 0; i < (selection?.rangeCount ?? 0); i += 1) {
    const range = selection?.getRangeAt(i);
    if (range) previousRanges.push(range);
  }

  const anchor = document.createElement("span");
  anchor.textContent = " ";
  anchor.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0";
  document.body.appendChild(anchor);

  document.addEventListener("copy", onCopy, true);
  try {
    selection?.removeAllRanges();
    const range = document.createRange();
    range.selectNodeContents(anchor);
    selection?.addRange(range);
    document.execCommand("copy");
  } catch {
    handled = false;
  } finally {
    document.removeEventListener("copy", onCopy, true);
    anchor.remove();
    selection?.removeAllRanges();
    for (const range of previousRanges) selection?.addRange(range);
  }
  return handled;
};

/**
 * 클립보드에 TSV(text/plain)와 표 마크업(text/html)을 함께 싣는다.
 * 엑셀은 text/html 을 우선 읽어 병합 셀을 복원하고, 메모장·우리 붙여넣기 경로 등
 * 평문만 읽는 대상은 TSV 를 쓴다.
 *
 * ClipboardItem 을 못 쓰거나(구형 브라우저·비보안 컨텍스트) 쓰기가 실패하면
 * 평문 쓰기로 내려간다 — 병합 복원은 부가 기능이라 복사 자체를 막지 않는다.
 */
const writeClipboardWithHtml = async (
  text: string,
  html: string | null,
): Promise<void> => {
  if (
    html &&
    typeof ClipboardItem !== "undefined" &&
    navigator.clipboard.write
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return;
    } catch {
      // 아래 평문 경로로 폴백
    }
  }
  await navigator.clipboard.writeText(text);
};

/**
 * 그리드를 알 수 없는 선택(여러 표에 걸침, 표 밖 셀)의 복사 폴백:
 * 화면 좌표(rect.top)로 행을 묶는다. 병합 셀 패딩은 없다.
 */
const buildRectClipboardRows = (cells: SelectableCellElement[]): string[][] => {
  const dataCells = cells
    .map((cell) => ({
      rect: cell.getBoundingClientRect(),
      text: getClipboardText(cell),
    }))
    .sort((a, b) => {
      const rowDiff = a.rect.top - b.rect.top;
      if (Math.abs(rowDiff) > ROW_THRESHOLD) return rowDiff;
      return a.rect.left - b.rect.left;
    });

  const rows: string[][] = [];
  let currentRow: string[] = [];
  let lastTop = NaN;

  dataCells.forEach(({ rect, text }) => {
    if (
      !Number.isNaN(lastTop) &&
      Math.abs(rect.top - lastTop) > ROW_THRESHOLD
    ) {
      if (currentRow.length > 0) {
        rows.push([...currentRow]);
        currentRow = [];
      }
    }
    currentRow.push(text);
    lastTop = rect.top;
  });
  if (currentRow.length > 0) rows.push(currentRow);
  return rows;
};

const areSelectionsEqual = (left: Set<string>, right: Set<string>): boolean => {
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
};

const ARROW_DELTAS: Record<string, { dr: number; dc: number }> = {
  ArrowUp: { dr: -1, dc: 0 },
  ArrowDown: { dr: 1, dc: 0 },
  ArrowLeft: { dr: 0, dc: -1 },
  ArrowRight: { dr: 0, dc: 1 },
};

// ── 드래그 자동 스크롤 헬퍼 ──

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * 포인터가 [start, end] 뷰포트의 가장자리(AUTO_SCROLL_EDGE_SIZE) 안/밖에 있을 때
 * 프레임당 스크롤량을 반환한다. 가장자리에 가까울수록 빠르고(최대 AUTO_SCROLL_MAX_SPEED),
 * edge zone 안쪽 경계에 가까울수록 0으로 감속한다. 뷰포트 밖이면 최대 속도.
 */
export const getAxisAutoScrollDelta = (
  pointerCoordinate: number,
  startCoordinate: number,
  endCoordinate: number,
): number => {
  const distanceFromStart = pointerCoordinate - startCoordinate;
  if (distanceFromStart < AUTO_SCROLL_EDGE_SIZE) {
    return -Math.min(
      AUTO_SCROLL_MAX_SPEED,
      AUTO_SCROLL_EDGE_SIZE - distanceFromStart,
    );
  }

  const distanceFromEnd = endCoordinate - pointerCoordinate;
  if (distanceFromEnd < AUTO_SCROLL_EDGE_SIZE) {
    return Math.min(
      AUTO_SCROLL_MAX_SPEED,
      AUTO_SCROLL_EDGE_SIZE - distanceFromEnd,
    );
  }

  return 0;
};

/** 점-사각형 최단 거리의 제곱(비교 전용이라 sqrt 생략) */
export const getPointToRectDistanceSq = (
  point: PointerPosition,
  rect: DOMRect,
): number => {
  const dx =
    point.x < rect.left
      ? rect.left - point.x
      : point.x > rect.right
        ? point.x - rect.right
        : 0;
  const dy =
    point.y < rect.top
      ? rect.top - point.y
      : point.y > rect.bottom
        ? point.y - rect.bottom
        : 0;
  return dx * dx + dy * dy;
};

const canScrollOnAxis = (element: HTMLElement, axis: ScrollAxis): boolean =>
  axis === "x"
    ? element.scrollWidth > element.clientWidth + SCROLL_EPSILON
    : element.scrollHeight > element.clientHeight + SCROLL_EPSILON;

const isScrollableOnAxis = (
  element: HTMLElement,
  axis: ScrollAxis,
): boolean => {
  const style = window.getComputedStyle(element);
  const overflow = axis === "x" ? style.overflowX : style.overflowY;
  return (
    canScrollOnAxis(element, axis) &&
    ["auto", "scroll", "overlay"].includes(overflow)
  );
};

/** 부모 체인을 1회만 순회해 x/y 스크롤 컨테이너를 동시에 찾는다. */
const findScrollContainers = (
  start: HTMLElement,
): Pick<DragSession, "scrollX" | "scrollY"> => {
  let scrollX: HTMLElement | null = null;
  let scrollY: HTMLElement | null = null;
  let current: HTMLElement | null = start;
  while (current && (!scrollX || !scrollY)) {
    if (!scrollX && isScrollableOnAxis(current, "x")) scrollX = current;
    if (!scrollY && isScrollableOnAxis(current, "y")) scrollY = current;
    current = current.parentElement;
  }

  const documentScroller = document.scrollingElement;
  if (documentScroller instanceof HTMLElement) {
    if (!scrollX && canScrollOnAxis(documentScroller, "x")) {
      scrollX = documentScroller;
    }
    if (!scrollY && canScrollOnAxis(documentScroller, "y")) {
      scrollY = documentScroller;
    }
  }

  return { scrollX, scrollY };
};

const getScrollViewportBounds = (
  scrollContainer: HTMLElement,
): ViewportBounds => {
  if (scrollContainer === document.scrollingElement) {
    return {
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0,
    };
  }
  const rect = scrollContainer.getBoundingClientRect();
  return {
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
  };
};

const getSessionAxes = (session: DragSession) =>
  [
    { container: session.scrollX, axis: "x" },
    { container: session.scrollY, axis: "y" },
  ] as const;

const getAutoScrollDelta = (
  pointer: PointerPosition,
  viewport: ViewportBounds,
  axis: ScrollAxis,
): number =>
  axis === "x"
    ? getAxisAutoScrollDelta(pointer.x, viewport.left, viewport.right)
    : getAxisAutoScrollDelta(pointer.y, viewport.top, viewport.bottom);

/** 포인터가 자동 스크롤 edge zone(뷰포트 가장자리/밖)에 있는지 — mousemove 마다 도는 사전 판정 */
const isInAutoScrollEdgeZone = (
  pointer: PointerPosition,
  session: DragSession,
): boolean =>
  getSessionAxes(session).some(
    ({ container, axis }) =>
      container !== null &&
      getAutoScrollDelta(pointer, getScrollViewportBounds(container), axis) !==
        0,
  );

/**
 * 선택 루트가 스크롤 방향으로 뷰포트 너머까지 이어져 있는지.
 * 셀 전체를 순회하는 대신 루트 rect 하나로 판정한다(성능).
 */
export const rootExtendsBeyondViewport = (
  rootRect: DOMRect,
  viewport: ViewportBounds,
  axis: ScrollAxis,
  delta: number,
): boolean =>
  axis === "x"
    ? delta > 0
      ? rootRect.right > viewport.right + SCROLL_EPSILON
      : rootRect.left < viewport.left - SCROLL_EPSILON
    : delta > 0
      ? rootRect.bottom > viewport.bottom + SCROLL_EPSILON
      : rootRect.top < viewport.top - SCROLL_EPSILON;

/** 포인터(뷰포트 밖일 수 있음)를 스크롤 뷰포트 안으로 클램프한 지점의 선택 가능 셀을 찾는다. */
const findSelectableCellFromPoint = (
  pointer: PointerPosition,
  session: DragSession,
): SelectableCellElement | null => {
  const xBounds = session.scrollX
    ? getScrollViewportBounds(session.scrollX)
    : null;
  const yBounds = session.scrollY
    ? getScrollViewportBounds(session.scrollY)
    : null;
  const x = clamp(
    pointer.x,
    xBounds ? xBounds.left + EDGE_INSET : 0,
    xBounds ? xBounds.right - EDGE_INSET : window.innerWidth - EDGE_INSET,
  );
  const y = clamp(
    pointer.y,
    yBounds ? yBounds.top + EDGE_INSET : 0,
    yBounds ? yBounds.bottom - EDGE_INSET : window.innerHeight - EDGE_INSET,
  );

  const direct = document.elementFromPoint(x, y)?.closest(session.selector);
  if (direct instanceof HTMLElement && session.root.contains(direct)) {
    return direct;
  }

  // sticky 헤더·셀 사이 경계 등이 클램프 지점을 덮으면 elementFromPoint 가 셀을 못 찾는다.
  // 이때만 최근접 셀 스캔으로 폴백한다. 셀 목록은 세션당 1회만 조회해 캐싱한다
  // (스크롤로 목록은 안 바뀌고 rect 만 변하므로 rect 는 매번 읽는다).
  session.cells ??= Array.from(
    session.root.querySelectorAll<HTMLElement>(session.selector),
  );
  let closestCell: SelectableCellElement | null = null;
  let closestDistanceSq = Infinity;
  for (const cell of session.cells) {
    const distanceSq = getPointToRectDistanceSq(
      { x, y },
      cell.getBoundingClientRect(),
    );
    if (distanceSq < closestDistanceSq) {
      closestDistanceSq = distanceSq;
      closestCell = cell;
    }
  }
  return closestCell;
};

/**
 * 셀 mousedown 이 활성 편집기를 닫는 규칙 — 두 mousedown 진입점(컨테이너 위임·셀별
 * React 핸들러)이 공유한다.
 *
 * 셀 mousedown 은 드래그 선택을 위해 preventDefault 하는데, 그러면 브라우저의 기본
 * 포커스 이동이 막혀 활성 편집기가 blur 되지 않는다. blur 로만 커밋하는 편집기
 * (InlineEditInput 의 input, DSD 리뷰 문단 편집·IXD 여러 줄 셀의 textarea)는 그 순간
 * 저장 기회를 잃고, 다음 편집 진입에서 언마운트되며 초안이 조용히 사라진다. 그래서
 * 여기서 직접 blur 한다. 대상 엘리먼트가 늘면(예: contenteditable) 이 한 곳만 고친다.
 */
const blurActiveEditor = () => {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    el.blur();
  }
};

export function useTableCellSelection(
  containerRef: RefObject<HTMLDivElement | null>,
  options?: UseTableCellSelectionOptions,
) {
  const selectionOwnerRef = useRef(Symbol("table-cell-selection-owner"));
  const selectedCellsRef = useRef<Set<string>>(new Set());
  const isSelectingRef = useRef(false);
  const selectionStartRef = useRef<SelectableCellElement | null>(null);
  const dragEndCellRef = useRef<SelectableCellElement | null>(null);
  const dragPointerRef = useRef<PointerPosition | null>(null);
  /** mousedown 지점. 자동 스크롤은 여기서 DRAG_START_THRESHOLD_PX 이상 움직인 뒤에만 */
  const dragOriginRef = useRef<PointerPosition | null>(null);
  const dragSessionRef = useRef<DragSession | null>(null);
  const dragMouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(
    null,
  );
  const autoScrollFrameRef = useRef<number | null>(null);
  const styleIdsRef = useRef<Set<string>>(new Set());
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /**
   * 표 밖(컨테이너 루트) 범위 선택에 쓸 셀 셀렉터. autoBindContainer 표는 cellSelector
   * 옵션을 그대로 따른다 — 셀이 아닌데 같은 속성 계약을 쓰는 요소(예: dsd-editor 본문
   * 값 아이템 span 도 id+data-cell-id 를 가짐)가 rect 선택에 섞이지 않게 한다.
   */
  const resolveContainerCellSelector = useCallback(
    () =>
      optionsRef.current?.autoBindContainer
        ? (optionsRef.current.cellSelector ??
          (optionsRef.current.includeHeaderCells
            ? DEFAULT_CELL_SELECTOR_WITH_HEADER
            : DEFAULT_CELL_SELECTOR))
        : "[data-cell-id][id]",
    [],
  );

  /**
   * 시작 표 안에서 선택 자격을 갖는 셀의 셀렉터. 헤더 포함 여부는 표별 옵션이라
   * 그리드 구성·범위 선택·방향키 이동이 모두 이 한 곳을 통해 결정된다.
   */
  const resolveTableScopedSelector = useCallback(
    () =>
      optionsRef.current?.includeHeaderCells
        ? TABLE_SCOPED_CELL_SELECTOR_WITH_HEADER
        : TABLE_SCOPED_CELL_SELECTOR,
    [],
  );

  const rangeAnchorRef = useRef<{ row: number; col: number } | null>(null);
  const rangeCursorRef = useRef<{ row: number; col: number } | null>(null);
  const rangeTableRef = useRef<HTMLTableElement | null>(null);

  const getStyleId = useCallback(
    () => optionsRef.current?.styleId ?? DEFAULT_SELECTION_STYLE_ID,
    [],
  );

  const applySelectionClasses = useCallback(
    (newIds: Set<string>) => {
      if (areSelectionsEqual(selectedCellsRef.current, newIds)) {
        return;
      }

      selectedCellsRef.current = newIds;
      const styleId = getStyleId();
      styleIdsRef.current.add(styleId);
      registerStyleOwner(styleId, selectionOwnerRef.current);
      let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }

      const entries = getSelectionEntries(newIds);
      if (newIds.size === 0) {
        styleEl.textContent = "";
        optionsRef.current?.onSelectionChange?.([], []);
        return;
      }

      // 시각 강조를 끈 경우(예: 검토 모드)에도 선택 추적/전파는 유지한다.
      if (optionsRef.current?.disableSelectionStyle) {
        styleEl.textContent = "";
        optionsRef.current?.onSelectionChange?.(Array.from(newIds), entries);
        return;
      }

      // 선택 셀 수가 minSelectionToStyle 미만이면 강조는 칠하지 않되(드래그가 1→2+ 로
      // 커질 수 있게 추적은 유지), 선택 변경은 그대로 전파한다.
      const minToStyle = optionsRef.current?.minSelectionToStyle ?? 1;
      if (newIds.size < minToStyle) {
        styleEl.textContent = "";
        optionsRef.current?.onSelectionChange?.(Array.from(newIds), entries);
        return;
      }

      const selectors = Array.from(newIds)
        .map((id) => `#${CSS.escape(id)}`)
        .join(",");
      const editedSelectors = Array.from(newIds)
        .map((id) => `#${CSS.escape(id)}[data-edited]`)
        .join(",");

      const relatedSelectors =
        // 동일값 강조는 단일 기준 셀에서만 적용한다.
        // 다중 선택은 서로 다른 identity를 섞을 수 있고 selector 집합도 과도하게 커진다.
        optionsRef.current?.enableRelatedCellSelection && newIds.size === 1
          ? (() => {
              const identityKeys = new Set<string>();
              const cellIds = new Set<string>();
              for (const uid of newIds) {
                const el = document.getElementById(uid);
                const identityKey = el?.getAttribute("data-cell-identity-key");
                if (identityKey) {
                  identityKeys.add(identityKey);
                  continue;
                }
                const cellId = el?.getAttribute("data-cell-id");
                if (cellId) cellIds.add(cellId);
              }
              const identitySelectors = Array.from(identityKeys).map(
                (key) => `[data-cell-identity-key="${CSS.escape(key)}"]`,
              );
              const cellIdSelectors = Array.from(cellIds).map(
                (cid) => `[data-cell-id="${CSS.escape(cid)}"]`,
              );
              return [...identitySelectors, ...cellIdSelectors].join(",");
            })()
          : "";

      styleEl.textContent =
        (relatedSelectors
          ? `${relatedSelectors}{background-color:rgb(165 243 252)!important;position:relative}`
          : "") +
        `${selectors}{${optionsRef.current?.selectionCss ?? DEFAULT_SELECTED_CELL_CSS}}` +
        `${editedSelectors}{background-color:rgb(199 210 254)!important}`;
      optionsRef.current?.onSelectionChange?.(Array.from(newIds), entries);
    },
    [getStyleId],
  );

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    if (dragMouseMoveHandlerRef.current) {
      document.removeEventListener(
        "mousemove",
        dragMouseMoveHandlerRef.current,
        { capture: true },
      );
      dragMouseMoveHandlerRef.current = null;
    }
    dragPointerRef.current = null;
    dragSessionRef.current = null;
  }, []);

  const clearSelection = useCallback(() => {
    applySelectionClasses(new Set());
    selectionStartRef.current = null;
    dragEndCellRef.current = null;
    isSelectingRef.current = false;
    stopAutoScroll();
    rangeAnchorRef.current = null;
    rangeCursorRef.current = null;
    rangeTableRef.current = null;
  }, [applySelectionClasses, stopAutoScroll]);

  const commitProgrammaticSelection = useCallback(
    (ids: Set<string>, anchor: SelectableCellElement | null) => {
      selectionStartRef.current = anchor;
      dragEndCellRef.current = anchor;
      isSelectingRef.current = false;
      stopAutoScroll();
      rangeAnchorRef.current = null;
      rangeCursorRef.current = null;
      rangeTableRef.current = null;
      applySelectionClasses(ids);
    },
    [applySelectionClasses, stopAutoScroll],
  );

  useEffect(() => {
    const owner = selectionOwnerRef.current;
    clearSelectionByOwner.set(owner, clearSelection);
    return () => {
      clearSelectionByOwner.delete(owner);
    };
  }, [clearSelection]);

  const copySelectedCells = useCallback(async () => {
    const selected = selectedCellsRef.current;
    if (selected.size === 0) return;

    const cells: SelectableCellElement[] = [];
    const missingDisplayValueIds: string[] = [];
    selected.forEach((uid) => {
      const cell = document.getElementById(uid);
      if (cell) {
        if (cell.getAttribute("data-display-value") === null) {
          missingDisplayValueIds.push(cell.id);
        }
        cells.push(cell);
      }
    });
    if (missingDisplayValueIds.length > 0) {
      Sentry.captureException(
        new Error(
          `data-display-value 누락 셀 ${missingDisplayValueIds.length}건 (예: ${missingDisplayValueIds.slice(0, 3).join(", ")}). selectableCellAttrs를 사용하세요.`,
        ),
      );
    }

    if (cells.length === 0) return;

    const format = optionsRef.current?.clipboardTableFormat ?? "values";
    // "excel-table" 만 그리드 좌표로 직렬화한다(병합 자리 "" 패딩). 기본은 예전처럼
    // 화면 좌표로 행을 묶는다 — 병합 셀을 한 칸으로 세던 기존 복사 모양을 지킨다.
    const rows =
      (format === "excel-table"
        ? buildGridClipboardRows(cells, format)
        : null) ?? buildRectClipboardRows(cells);
    // 같은 선택을 표 마크업으로도 실어 엑셀이 병합 셀을 그대로 복원하게 한다.
    const html = buildCopyClipboardHtml(cells, format, rows);
    const text = serializeRowsToClipboardText(rows);

    // copy 이벤트 경로를 먼저 쓴다. keydown(사용자 제스처) 안에서 **동기로** 실행돼야
    // 하므로 await 보다 앞에 둔다 — 이 경로만 표 마크업이 새니타이저를 통과한다.
    // 실을 마크업이 없으면 쓰지 않는다: 이 경로는 창의 텍스트 선택을 잠깐 바꿨다
    // 되돌리므로, 평문만 싣던 기존 화면에까지 부작용을 줄 이유가 없다.
    if (html && writeClipboardViaCopyEvent(text, html)) {
      toast.success(`선택한 영역의 ${cells.length}개 셀이 복사되었습니다.`);
      clearSelection();
      return;
    }

    try {
      await writeClipboardWithHtml(text, html);
      toast.success(`선택한 영역의 ${cells.length}개 셀이 복사되었습니다.`);
      clearSelection();
    } catch (error) {
      console.error("복사 실패:", error);
    }
  }, [clearSelection]);

  /**
   * 클립보드 평문을 선택 영역에 적용한다. 규칙은 이 제품의 JSpreadsheet 붙여넣기와 같다
   * (`libs/jspreadsheet-ce/src/utils/copyPaste.js`): 선택 폭이 블록 폭의 정수배면 가로로,
   * 선택 높이가 블록 높이의 정수배면 세로로 각각 반복한다. 아니면 앵커에 블록을 한 번 놓는다.
   * 좌표는 rowSpan/colSpan 을 펼친 그리드라 병합 셀 옆 열이 밀리지 않는다. 병합 셀은
   * anchor 좌표의 값만 받고, 덮인 좌표는 건너뛴다.
   */
  const pasteClipboardText = useCallback(
    (clipText: string) => {
      const selected = selectedCellsRef.current;
      if (selected.size === 0 || !containerRef.current) return;
      const onPasteValues = optionsRef.current?.onPasteValues;
      if (!onPasteValues) return;

      const pastedRows = parsePastedClipboardText(clipText);
      if (pastedRows.length === 0) return;

      const anchorCell = findTopLeftCell(selected);
      if (!(anchorCell instanceof HTMLTableCellElement)) return;
      const table = anchorCell.closest("table");
      if (!table) return;

      const { occupancy, anchorOf } = buildSpanGrid(table);
      const pos = anchorOf.get(anchorCell);
      if (!pos) return;

      let maxRow = pos.row;
      let maxCol = pos.col;
      anchorOf.forEach((cellPos, cell) => {
        if (!selected.has(cell.id)) return;
        maxRow = Math.max(maxRow, cellPos.row + Math.max(1, cell.rowSpan) - 1);
        maxCol = Math.max(maxCol, cellPos.col + Math.max(1, cell.colSpan) - 1);
      });
      const blockRows = pastedRows.length;
      const blockCols = Math.max(...pastedRows.map((row) => row.length));
      const selectionRows = maxRow - pos.row + 1;
      const selectionCols = maxCol - pos.col + 1;
      const targetRows =
        selectionRows % blockRows === 0 ? selectionRows : blockRows;
      const targetCols =
        selectionCols % blockCols === 0 ? selectionCols : blockCols;

      const entries: TableCellPasteEntry[] = [];
      let unusable = 0;
      for (let r = 0; r < targetRows; r += 1) {
        const values = pastedRows[r % blockRows];
        for (let c = 0; c < targetCols; c += 1) {
          const element = occupancy[pos.row + r]?.[pos.col + c];
          const cellAnchor = element && anchorOf.get(element);
          if (
            cellAnchor &&
            (cellAnchor.row !== pos.row + r || cellAnchor.col !== pos.col + c)
          ) {
            continue; // 병합 셀이 덮은 좌표 — anchor 에서 이미 처리
          }
          const cellId = element?.getAttribute("data-cell-id");
          if (!element || !cellId) {
            unusable += 1;
            continue;
          }
          entries.push({
            cellId,
            uniqueId: element.id,
            element,
            value: values[c % blockCols] ?? "",
          });
        }
      }

      if (entries.length === 0) return;
      const { applied, failed } = onPasteValues(entries);
      // 실패만 있으면 사유 안내는 소비처가 띄우므로 여기서는 침묵한다.
      if (applied === 0 && failed > 0) return;
      // 엑셀처럼 붙여넣은 범위를 선택 상태로 둔다 — 어디까지 들어갔는지 바로 보인다.
      commitProgrammaticSelection(
        new Set(entries.map((entry) => entry.uniqueId)),
        anchorCell,
      );
      // 전부 기존 값과 같아 바뀐 게 없어도 반응은 있어야 한다 — 조용한 실패 금지.
      // 문구는 값 채우기 화면의 붙여넣기 버튼(paste-match-button)과 같다.
      if (applied === 0) {
        toast.info("모든 항목 값이 이미 일치합니다.");
        return;
      }
      toastApplyResult({ applied, notApplied: failed + unusable });
    },
    [commitProgrammaticSelection, containerRef],
  );

  const selectRect = useCallback(
    (
      grid: HTMLTableCellElement[][],
      anchor: { row: number; col: number },
      cursor: { row: number; col: number },
    ): Set<string> => {
      const minR = Math.min(anchor.row, cursor.row);
      const maxR = Math.max(anchor.row, cursor.row);
      const minC = Math.min(anchor.col, cursor.col);
      const maxC = Math.max(anchor.col, cursor.col);
      const ids = new Set<string>();
      for (let r = minR; r <= maxR; r += 1) {
        if (!grid[r]) continue;
        for (let c = minC; c <= maxC; c += 1) {
          const td = grid[r][c];
          if (td?.id) ids.add(td.id);
        }
      }
      return ids;
    },
    [],
  );

  const moveSelection = useCallback(
    (direction: string, shift: boolean) => {
      const selected = selectedCellsRef.current;
      if (selected.size === 0 || !containerRef.current) return;

      const delta = ARROW_DELTAS[direction];
      if (!delta) return;

      if (shift) {
        if (!rangeAnchorRef.current || !rangeTableRef.current) {
          if (selected.size === 1) {
            const uid = selected.values().next().value as string;
            const selectedElement = document.getElementById(uid);
            const td =
              selectedElement instanceof HTMLTableCellElement
                ? selectedElement
                : null;
            if (!td) return;
            const table = td.closest("table") as HTMLTableElement | null;
            if (!table) return;
            const { findCell } = buildTableGrid(
              table,
              resolveTableScopedSelector(),
            );
            const pos = findCell(td);
            if (!pos) return;
            rangeAnchorRef.current = pos;
            rangeCursorRef.current = pos;
            rangeTableRef.current = table;
          } else {
            const anchor = findTopLeftCell(selected);
            if (!(anchor instanceof HTMLTableCellElement)) return;
            const table = anchor.closest("table") as HTMLTableElement | null;
            if (!table) return;
            const { findCell } = buildTableGrid(
              table,
              resolveTableScopedSelector(),
            );
            const anchorPos = findCell(anchor);
            if (!anchorPos) return;
            let maxRow = anchorPos.row;
            let maxCol = anchorPos.col;
            selected.forEach((uid) => {
              const element = document.getElementById(uid);
              if (!(element instanceof HTMLTableCellElement)) return;
              const el = element;
              const pos = findCell(el);
              if (pos) {
                if (pos.row > maxRow) maxRow = pos.row;
                if (pos.col > maxCol) maxCol = pos.col;
              }
            });
            rangeAnchorRef.current = anchorPos;
            rangeCursorRef.current = { row: maxRow, col: maxCol };
            rangeTableRef.current = table;
          }
        }

        const { grid } = buildTableGrid(
          rangeTableRef.current,
          resolveTableScopedSelector(),
        );
        const cursor = rangeCursorRef.current!;
        const newRow = cursor.row + delta.dr;
        const newCol = cursor.col + delta.dc;
        if (!grid[newRow]?.[newCol]) return;

        rangeCursorRef.current = { row: newRow, col: newCol };
        applySelectionClasses(
          selectRect(grid, rangeAnchorRef.current!, rangeCursorRef.current),
        );
        grid[newRow][newCol]?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
        return;
      }

      let startTd: HTMLTableCellElement | null = null;
      if (selected.size !== 1) {
        if (rangeCursorRef.current && rangeTableRef.current) {
          const { grid: rGrid } = buildTableGrid(
            rangeTableRef.current,
            resolveTableScopedSelector(),
          );
          startTd =
            rGrid[rangeCursorRef.current.row]?.[rangeCursorRef.current.col] ??
            null;
        }
        if (
          !startTd &&
          dragEndCellRef.current instanceof HTMLTableCellElement
        ) {
          startTd = dragEndCellRef.current;
        }
      }
      if (!startTd) {
        const uid = selected.values().next().value as string;
        const selectedElement = document.getElementById(uid);
        startTd =
          selectedElement instanceof HTMLTableCellElement
            ? selectedElement
            : null;
      }

      rangeAnchorRef.current = null;
      rangeCursorRef.current = null;
      rangeTableRef.current = null;

      if (!startTd) return;

      const table = startTd.closest("table");
      if (!table) return;

      const { grid, findCell } = buildTableGrid(
        table,
        resolveTableScopedSelector(),
      );
      const pos = findCell(startTd);
      if (!pos) return;

      const targetTd = grid[pos.row + delta.dr]?.[pos.col + delta.dc];
      if (!targetTd || !targetTd.id) {
        if (startTd.id) applySelectionClasses(new Set([startTd.id]));
        return;
      }

      applySelectionClasses(new Set([targetTd.id]));
      targetTd.scrollIntoView({ block: "nearest", inline: "nearest" });
    },
    [
      containerRef,
      applySelectionClasses,
      selectRect,
      resolveTableScopedSelector,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (activeSelectionOwner !== selectionOwnerRef.current) return;
      if (isEditableTarget(event.target)) return;

      // undo는 셀 선택과 무관하게 (편집 후 다른 곳 클릭으로 선택이 풀려도)
      // 동작해야 하므로 selection 가드 위에서 먼저 처리한다.
      if (
        (event.ctrlKey || event.metaKey) &&
        event.code === "KeyZ" &&
        !event.shiftKey
      ) {
        const onUndo = optionsRef.current?.onUndo;
        if (onUndo) {
          event.preventDefault();
          onUndo();
        }
        return;
      }

      const selected = selectedCellsRef.current;
      if (selected.size === 0) return;

      if (event.ctrlKey || event.metaKey) {
        if (event.code === "KeyC") {
          // 사용자가 텍스트를 드래그 선택한 경우(예: floating panel 의 계산 결과)
          // 셀 복사로 가로채지 않고 브라우저 네이티브 복사에 양보한다. 셀 선택은
          // CSS 클래스로만 표시되므로 이 시점의 텍스트 선택은 드래그한 실제 텍스트다.
          if ((window.getSelection()?.toString().trim().length ?? 0) > 0) {
            return;
          }
          event.preventDefault();
          copySelectedCells();
          return;
        }
      }

      if (event.key === "Escape") {
        event.preventDefault();
        clearSelection();
        return;
      }

      if (
        (optionsRef.current?.enableArrowNavigation ?? true) &&
        event.key in ARROW_DELTAS
      ) {
        event.preventDefault();
        moveSelection(event.key, event.shiftKey);
        return;
      }

      if (event.key === "Enter" && !event.isComposing && selected.size >= 1) {
        event.preventDefault();
        let targetCell: SelectableCellElement | null = null;
        let targetUid = "";
        if (rangeCursorRef.current && rangeTableRef.current) {
          const { grid } = buildTableGrid(
            rangeTableRef.current,
            resolveTableScopedSelector(),
          );
          targetCell =
            grid[rangeCursorRef.current.row]?.[rangeCursorRef.current.col] ??
            null;
          targetUid = targetCell?.id || "";
        } else if (selected.size === 1) {
          targetUid = selected.values().next().value as string;
          targetCell = document.getElementById(targetUid);
        } else {
          const anchor = findTopLeftCell(selected);
          targetCell = anchor;
          targetUid = anchor?.id || "";
        }

        if (targetUid && targetCell) {
          applySelectionClasses(new Set([targetUid]));
          rangeAnchorRef.current = null;
          rangeCursorRef.current = null;
          rangeTableRef.current = null;
          const cellId = targetCell.getAttribute("data-cell-id") || "";
          if (cellId) {
            optionsRef.current?.onEnterEdit?.(cellId, targetUid, targetCell);
          }
        }
        return;
      }

      if (
        (event.key === "Backspace" || event.key === "Delete") &&
        !event.isComposing
      ) {
        const onDeleteValues = optionsRef.current?.onDeleteValues;
        if (!onDeleteValues) return;
        event.preventDefault();
        const entries = getSelectionEntries(selected);
        if (entries.length > 0) {
          onDeleteValues(
            entries.map((entry) => entry.cellId),
            entries,
          );
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    applySelectionClasses,
    copySelectedCells,
    resolveTableScopedSelector,
    clearSelection,
    moveSelection,
  ]);

  // 붙여넣기는 `paste` 이벤트로 받는다 — 복사의 `copy` 이벤트 경로와 대칭이고, 단축키·
  // 메뉴 어느 쪽이든 권한 프롬프트 없이 clipboardData 가 온다. 편집 중인 입력 요소는
  // 브라우저 기본 동작에 맡긴다.
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (activeSelectionOwner !== selectionOwnerRef.current) return;
      if (isEditableTarget(event.target)) return;
      if (!optionsRef.current?.onPasteValues) return;
      if (selectedCellsRef.current.size === 0) return;
      // getData 는 형식이 없어도 "" 를 돌려준다. 이미지·파일만 든 클립보드를 "빈 셀
      // 복사"로 오해해 선택 셀을 지우지 않도록 형식 존재부터 확인한다.
      const clipboardData = event.clipboardData;
      if (
        !clipboardData ||
        !Array.from(clipboardData.types).includes("text/plain")
      ) {
        return;
      }
      event.preventDefault();
      pasteClipboardText(clipboardData.getData("text/plain"));
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [pasteClipboardText]);

  useEffect(() => {
    const owner = selectionOwnerRef.current;
    const styleIds = styleIdsRef.current;
    return () => {
      const ownsActiveSelection = activeSelectionOwner === owner;
      if (ownsActiveSelection) {
        styleIds.forEach((styleId) => {
          const styleElement = document.getElementById(styleId);
          if (styleElement) styleElement.textContent = "";
        });
      }
      styleIds.forEach((styleId) => {
        if (unregisterStyleOwner(styleId, owner)) {
          document.getElementById(styleId)?.remove();
        }
      });
      if (ownsActiveSelection) activeSelectionOwner = null;
    };
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      if (activeSelectionOwner !== selectionOwnerRef.current) return;
      isSelectingRef.current = false;
      stopAutoScroll();
    };
    document.addEventListener("mouseup", handleMouseUp, { capture: true });
    return () => {
      document.removeEventListener("mouseup", handleMouseUp, {
        capture: true,
      });
      // unmount 시 예약된 rAF 정리
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  useEffect(() => {
    const handleMouseDownClearSelection = (event: MouseEvent) => {
      if (activeSelectionOwner !== selectionOwnerRef.current) return;
      const container = containerRef.current;
      if (!container) return;

      const target = event.target;
      if (!(target instanceof Element)) {
        clearSelection();
        return;
      }

      if (target.closest("[data-keep-cell-selection]")) return;
      if (!container.contains(target)) {
        clearSelection();
        return;
      }

      if (
        target.closest('input, textarea, select, button, a, [role="button"]')
      ) {
        return;
      }

      if (target.closest("[data-cell-id][id]")) return;

      if (selectedCellsRef.current.size > 0) {
        clearSelection();
      }
    };
    document.addEventListener("mousedown", handleMouseDownClearSelection);
    return () =>
      document.removeEventListener("mousedown", handleMouseDownClearSelection);
  }, [containerRef, clearSelection]);

  // ── 드래그 선택 코어 (React 핸들러 / 컨테이너 위임 둘 다 이 코어를 공유) ──

  /**
   * 드래그 확장. `enableGridRangeSelection` 을 켠 표는 같은 표 안 드래그를 엑셀 그리드
   * 시맨틱(anchor~target 사각 범위 + 병합 셀 closure)으로 선택한다 — rect 겹침율로는
   * 전폭 colspan 셀이 좁은 드래그에서 빠져 선택이 중간에 끊긴다.
   * 옵션이 꺼져 있거나 표 밖/크로스 표면 rect 50% 겹침 규칙으로 폴백.
   */
  const extendSelectionTo = useCallback(
    (target: SelectableCellElement) => {
      if (!selectionStartRef.current || !containerRef.current) return;
      dragEndCellRef.current = target;

      if (optionsRef.current?.enableGridRangeSelection) {
        const gridSelection = collectGridRangeSelection(
          selectionStartRef.current,
          target,
          resolveTableScopedSelector(),
        );
        if (gridSelection) {
          applySelectionClasses(gridSelection);
          return;
        }
      }

      const startRect = selectionStartRef.current.getBoundingClientRect();
      const endRect = target.getBoundingClientRect();

      const minX = Math.min(startRect.left, endRect.left);
      const maxX = Math.max(startRect.right, endRect.right);
      const minY = Math.min(startRect.top, endRect.top);
      const maxY = Math.max(startRect.bottom, endRect.bottom);

      const startTable = selectionStartRef.current.closest("table");
      const targetTable = target.closest("table");
      const selectionRoot =
        startTable && targetTable && startTable === targetTable
          ? startTable
          : containerRef.current;
      const selectableSelector =
        selectionRoot instanceof HTMLTableElement
          ? resolveTableScopedSelector()
          : resolveContainerCellSelector();
      const allSelectableCells = selectionRoot.querySelectorAll(
        selectableSelector,
      ) as NodeListOf<SelectableCellElement>;

      const newSelected = new Set<string>();
      allSelectableCells.forEach((cell) => {
        const cellRect = cell.getBoundingClientRect();
        const overlapW = Math.max(
          0,
          Math.min(cellRect.right, maxX) - Math.max(cellRect.left, minX),
        );
        const overlapH = Math.max(
          0,
          Math.min(cellRect.bottom, maxY) - Math.max(cellRect.top, minY),
        );
        const overlapArea = overlapW * overlapH;
        const cellArea = cellRect.width * cellRect.height;
        if (cellArea > 0 && overlapArea / cellArea >= 0.5 && cell.id) {
          newSelected.add(cell.id);
        }
      });

      applySelectionClasses(newSelected);
    },
    [
      containerRef,
      applySelectionClasses,
      resolveContainerCellSelector,
      resolveTableScopedSelector,
    ],
  );

  // ── 드래그 자동 스크롤: 포인터가 스크롤 뷰포트 가장자리/밖이면 컨테이너를 밀어주며 선택을 확장 ──

  const runAutoScroll = useCallback(() => {
    const pointer = dragPointerRef.current;
    const session = dragSessionRef.current;
    if (!isSelectingRef.current || !pointer || !session) {
      stopAutoScroll();
      return;
    }

    // 성능: "더 선택할 영역이 남았는지"를 셀 전체 순회 대신 루트 rect 1회로 판정한다.
    // 루트가 스크롤 컨테이너 자신이면 rect 판정이 무의미하므로 스크롤 한계 클램프에 맡긴다.
    const rootRect = session.root.getBoundingClientRect();
    let didAutoScroll = false;

    for (const { container, axis } of getSessionAxes(session)) {
      if (!container) continue;
      const viewport = getScrollViewportBounds(container);
      const delta = getAutoScrollDelta(pointer, viewport, axis);
      if (delta === 0) continue;
      if (
        session.root !== container &&
        !rootExtendsBeyondViewport(rootRect, viewport, axis, delta)
      ) {
        continue;
      }
      const previous =
        axis === "x" ? container.scrollLeft : container.scrollTop;
      if (axis === "x") container.scrollLeft += delta;
      else container.scrollTop += delta;
      const current = axis === "x" ? container.scrollLeft : container.scrollTop;
      if (current !== previous) didAutoScroll = true;
    }

    const target = findSelectableCellFromPoint(pointer, session);
    // 타겟이 직전 프레임과 같으면 선택 집합도 같으므로(앵커·타겟이 함께 스크롤됨)
    // O(전체 셀) 재계산을 건너뛴다.
    if (target && target !== dragEndCellRef.current) extendSelectionTo(target);

    // 실제로 스크롤이 일어난 동안만 루프를 지속한다(한계 도달·edge zone 이탈 시 자연 종료).
    autoScrollFrameRef.current = didAutoScroll
      ? window.requestAnimationFrame(runAutoScroll)
      : null;
  }, [extendSelectionTo, stopAutoScroll]);

  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) return;
    autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
  }, [runAutoScroll]);

  // 뷰포트 밖에서는 셀 mouseenter/mouseover 가 발화하지 않으므로 document mousemove 로 보완한다.
  // 일반 드래그 경로(mouseenter)와 중복 작업하지 않도록 edge zone 에서만 rAF 루프를 시작한다.
  const handleDragMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isSelectingRef.current) return;
      if (event.buttons !== 1) {
        isSelectingRef.current = false;
        stopAutoScroll();
        return;
      }
      const session = dragSessionRef.current;
      if (!session) return;

      const pointer = { x: event.clientX, y: event.clientY };
      dragPointerRef.current = pointer;
      const origin = dragOriginRef.current;
      if (origin && !hasMovedPastDragThreshold(origin, pointer)) return;
      if (isInAutoScrollEdgeZone(pointer, session)) startAutoScroll();
    },
    [startAutoScroll, stopAutoScroll],
  );

  /** 드래그 시작: anchor 셀 하나만 선택한 상태로 만들고, 자동 스크롤용 세션을 캐싱한다. */
  const beginSelection = useCallback(
    (cell: SelectableCellElement, origin: PointerPosition | null = null) => {
      activateSelectionOwner(selectionOwnerRef.current);
      stopAutoScroll(); // 이전 드래그 잔여 상태(rAF·리스너·세션) 정리
      dragOriginRef.current = origin;
      // mousedown preventDefault 로 브라우저 기본 동작(기존 텍스트 선택 해제)까지 막히므로
      // 직접 해제한다 — 남은 텍스트 선택이 있으면 Ctrl+C 가 네이티브 복사에 양보해
      // (keydown 의 getSelection 가드) 셀 복사가 조용히 무시된다.
      window.getSelection()?.removeAllRanges();
      isSelectingRef.current = true;
      selectionStartRef.current = cell;
      rangeAnchorRef.current = null;
      rangeCursorRef.current = null;
      rangeTableRef.current = null;

      // 드래그 동안 불변인 정보(스크롤 컨테이너·선택 루트)를 1회만 계산한다.
      // 루트는 시작 테이블 기준(의도된 제품 동작): 자동 스크롤이 시작 표를 넘어
      // 다른 표까지 달리지 않도록 테이블 rect 게이트로 표 경계에서 멈춘다.
      // 표를 가로지르는 선택은 기존 mouseenter/mouseover 경로에서만 허용한다.
      const startTable = cell.closest("table");
      const root = startTable ?? containerRef.current;
      dragSessionRef.current = root
        ? {
            ...findScrollContainers(cell),
            root,
            selector: startTable
              ? resolveTableScopedSelector()
              : resolveContainerCellSelector(),
            cells: null,
          }
        : null;

      // mousemove 는 가장 빈발하는 이벤트라 상시 리스너로 두지 않고 드래그 중에만 붙인다
      // (파일 상단 owner 주석의 다중 마운트 비용 문제와 동일한 이유).
      if (dragSessionRef.current) {
        dragMouseMoveHandlerRef.current = handleDragMouseMove;
        document.addEventListener("mousemove", handleDragMouseMove, {
          capture: true,
        });
      }

      if (cell.id) applySelectionClasses(new Set([cell.id]));
    },
    [
      applySelectionClasses,
      containerRef,
      resolveTableScopedSelector,
      handleDragMouseMove,
      resolveContainerCellSelector,
      stopAutoScroll,
    ],
  );

  // 셀별 React 핸들러(IXD 등 기존 방식). 코어에 위임한다.
  const handleCellMouseDown = useCallback(
    (_uniqueId: string, event: React.MouseEvent) => {
      blurActiveEditor();
      event.preventDefault();
      beginSelection(event.currentTarget as SelectableCellElement, {
        x: event.clientX,
        y: event.clientY,
      });
    },
    [beginSelection],
  );

  const handleCellMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      if (!isSelectingRef.current) return;
      const isLeftButtonPressed =
        event.buttons === 1 || event.nativeEvent.buttons === 1;
      if (!isLeftButtonPressed) return;
      event.preventDefault();
      extendSelectionTo(event.currentTarget as SelectableCellElement);
    },
    [extendSelectionTo],
  );

  const handleCellMouseUp = useCallback(() => {
    isSelectingRef.current = false;
  }, []);

  // 컨테이너 위임 방식(autoBindContainer): 셀별 핸들러 없이 cellSelector 매칭 셀을 자동 선택.
  const autoBindContainer = options?.autoBindContainer ?? false;
  const cellSelector =
    options?.cellSelector ??
    (options?.includeHeaderCells
      ? DEFAULT_CELL_SELECTOR_WITH_HEADER
      : DEFAULT_CELL_SELECTOR);
  useEffect(() => {
    if (!autoBindContainer) return;
    const container = containerRef.current;
    if (!container) return;

    const resolveCell = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null;
      // 셀 안 편집 인풋에서 시작한 이벤트는 셀 선택으로 삼키지 않는다 — mousedown
      // preventDefault 가 caret 배치를 막고, 활성 인풋 blur 가 blur-커밋형 편집기
      // (예: dsd-editor InlineEditInput)의 저장을 오발시킨다. 셀별 핸들러 방식(IXD)은
      // 셀이 isEditing 을 알아 스스로 위임을 끊지만, 컨테이너 위임은 여기서 걸러야 한다.
      if (target.closest("input, textarea, select")) return null;
      if (target instanceof HTMLElement && target.isContentEditable)
        return null;
      const cell = target.closest(cellSelector);
      return cell instanceof HTMLElement ? cell : null;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return;
      const cell = resolveCell(event.target);
      if (!cell?.id) return;
      blurActiveEditor();
      event.preventDefault();
      beginSelection(cell, { x: event.clientX, y: event.clientY });
    };
    const onMouseOver = (event: MouseEvent) => {
      if (!isSelectingRef.current || event.buttons !== 1) return;
      const cell = resolveCell(event.target);
      if (!cell) return;
      extendSelectionTo(cell);
    };
    const onMouseUp = () => {
      isSelectingRef.current = false;
    };

    container.addEventListener("mousedown", onMouseDown);
    container.addEventListener("mouseover", onMouseOver);
    container.addEventListener("mouseup", onMouseUp);
    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      container.removeEventListener("mouseover", onMouseOver);
      container.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    autoBindContainer,
    cellSelector,
    containerRef,
    beginSelection,
    extendSelectionTo,
  ]);

  // 프로그래매틱 선택의 공통 처리: 드래그/범위 상태를 초기화하고 anchor 를 잡은 뒤 강조를 적용한다.
  const selectCell = useCallback(
    (uniqueId: string, options?: SelectCellOptions): boolean => {
      const element = document.getElementById(uniqueId);
      if (!element || !containerRef.current?.contains(element)) {
        return false;
      }

      activateSelectionOwner(selectionOwnerRef.current);
      commitProgrammaticSelection(new Set([uniqueId]), element);

      if (options?.scroll !== false) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return true;
    },
    [commitProgrammaticSelection, containerRef],
  );

  /**
   * 여러 셀을 한 번에 선택한다(예: 헤더 클릭 → 대응 셀 일괄 선택).
   * 컨테이너 밖이거나 존재하지 않는 uniqueId 는 무시한다. 선택이 비면 false.
   */
  const selectCells = useCallback(
    (uniqueIds: string[]): boolean => {
      const container = containerRef.current;
      if (!container) return false;
      const valid = uniqueIds.filter((id) => {
        const el = document.getElementById(id);
        return !!el && container.contains(el);
      });
      if (valid.length === 0) return false;

      activateSelectionOwner(selectionOwnerRef.current);
      commitProgrammaticSelection(
        new Set(valid),
        document.getElementById(valid[0]),
      );
      return true;
    },
    [commitProgrammaticSelection, containerRef],
  );

  return {
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    selectCell,
    selectCells,
    clearSelection,
  };
}
